import { LocaleEnum } from '@systemeio/ui'
import { isClient, isSioHost } from '@systemeio/util'
import { AvailablePlansEnum } from 'modules/profile/user-plan/enums/user-plan-type-enum'
import { useMemo } from 'react'
import { getLanguageCodeByLocale } from 'shared/enums/language-code-enum'
import { AdminRolesEnum, UserRolesEnum } from 'shared/enums/user-roles-enum'
import { UserWorkspaceRolesEnum } from 'shared/enums/user-workspace-roles-enum'
import { UserInterface } from 'shared/types/user-interface'
import { hasWorkspaces } from '../utils/has-workspaces'

export const useUserStatus = (dashboardUser?: UserInterface) => {
  const isCustomer = dashboardUser?.roles.includes(UserRolesEnum.customer) || false

  const isAffiliate = dashboardUser?.roles.includes(UserRolesEnum.affiliate) || false

  const isStudent = dashboardUser?.roles.includes(UserRolesEnum.student) || false

  const isWorkspaceOwner = dashboardUser ? dashboardUser.workspaceOwner === null : false

  const isRootAdmin = dashboardUser?.id === 161 || dashboardUser?.workspaceOwner === 161

  const isWhiteLabelOwner = (dashboardUser?.hasWhiteLabel && !isRootAdmin) || false

  const isSupportImpersonation =
    !!dashboardUser?.impersonator &&
    dashboardUser.impersonator.id !== dashboardUser.mainUser?.user.id

  const isSubAccountImpersonation = dashboardUser?.id !== dashboardUser?.mainUser?.user.id

  const checkAdminRoles = (adminRoles?: AdminRolesEnum[]) =>
    (adminRoles || Object.values(AdminRolesEnum)).filter(el => dashboardUser?.roles.includes(el))
      .length !== 0

  const shouldShowSubAccountPage =
    !isSubAccountImpersonation && isClient() && isSioHost(window.location.host)

  const checkImpersonatorAdminRoles = (adminRoles?: AdminRolesEnum[]) =>
    (adminRoles || Object.values(AdminRolesEnum)).filter(el =>
      dashboardUser?.impersonator?.roles.includes(el),
    ).length !== 0

  const isWsAdmin = useMemo(() => {
    if (!isWorkspaceOwner && dashboardUser && hasWorkspaces(dashboardUser)) {
      const workspace = dashboardUser.workspaceMemberships.find(
        el => el.workspaceOwner.id === dashboardUser.workspaceOwner,
      )
      return workspace ? workspace.role === UserWorkspaceRolesEnum.workspaceAdmin : false
    } else {
      return false
    }
  }, [dashboardUser, isWorkspaceOwner])

  const isWsAssistant = useMemo(() => {
    if (!isWorkspaceOwner && dashboardUser && hasWorkspaces(dashboardUser)) {
      const workspace = dashboardUser.workspaceMemberships.find(
        el => el.workspaceOwner.id === dashboardUser.workspaceOwner,
      )
      return workspace ? workspace.role === UserWorkspaceRolesEnum.workspaceAssistant : false
    } else {
      return false
    }
  }, [dashboardUser, isWorkspaceOwner])

  const intlLocale = useMemo(
    () => getLanguageCodeByLocale(dashboardUser?.dashboardLocale ?? LocaleEnum.ENGLISH),
    [dashboardUser?.dashboardLocale],
  )

  const isRestrictedUser = dashboardUser?.accessRestricted && isWorkspaceOwner

  const isSioPlanSubscriber = dashboardUser
    ? !!dashboardUser.userPlanId &&
      [
        AvailablePlansEnum.startupNew,
        AvailablePlansEnum.webinar,
        AvailablePlansEnum.enterprise,
      ].includes(dashboardUser.userPlanId)
    : undefined

  return {
    isCustomer,
    isAffiliate,
    isStudent,
    isWsAdmin,
    isWhiteLabelOwner,
    isWsAssistant,
    isWorkspaceOwner,
    isRootAdmin,
    isSubAccountImpersonation,
    isSupportImpersonation,
    checkAdminRoles,
    checkImpersonatorAdminRoles,
    intlLocale,
    shouldShowSubAccountPage,
    isRestrictedUser,
    isSioPlanSubscriber,
  }
}
